import React from 'react';
import { 
  Calculator, ShieldCheck, Scale, Award, 
  ChevronLeft, FileText, Landmark, BarChart3, 
  HelpCircle, ShieldAlert, BadgeDollarSign 
} from 'lucide-react'; // run: npm i lucide-react

export default function CostEstimation() {
  
  // Methodological estimation factors
  const estimationFactors = [
    {
      title: "تحديد نطاق العمل بدقة (Scope of Work)",
      desc: "نلتزم برسم حدود دقيقة لكافة الخدمات المطلوبة في العقد (عدد الجلسات، المذكرات، نوع الاستشارات الجانبية) لضمان عدم تحميل العميل أي تكاليف إضافية خارج النطاق المتفق عليه.",
      icon: <FileText className="w-6 h-6 text-amber-600" />
    },
    {
      title: "تقييم درجة التعقيد وحجم الأصول",
      desc: "تخضع الملفات لدراسة أولية لتقدير حجم المستندات المراد فحصها، وعدد الأطراف المتداخلة، والقيمة المالية المتنازع عليها، مما يسمح بتقديم تسعير عادل ومتناسب مع الجهد المبذول.",
      icon: <BarChart3 className="w-6 h-6 text-amber-600" />
    },
    {
      title: "تحليل المخاطر والسوابق القضائية",
      desc: "نبحث في فرص نجاح القضية والحلول الودية المتاحة قبل البدء بالتقاضي لنوفر على منشأتك النفقات القضائية غير الضرورية، وتوجيه رأس المال نحو المسار النظامي الأكثر أماناً.",
      icon: <Scale className="w-6 h-6 text-amber-600" />
    },
    {
      title: "التنسيق مع الخبراء والمقيمين المعتمدين",
      desc: "في قضايا التركات أو الملكية الفكرية والإفلاس الكبرى، نقوم بتقدير أجور المحاسبين القانونيين والمقيمين العقاريين مسبقاً وإدراجها بوضوح في ميزانية الملف التقديرية.",
      icon: <Landmark className="w-6 h-6 text-amber-600" />
    }
  ];

  return (
    <div className="bg-white text-right font-sans" dir="rtl">
      
      {/* Hero Header Section */}
      <section className="relative bg-slate-900 text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-15 bg-[url('https://images.unsplash.com/photo-1554224155-6b9931d0562e?auto=format&fit=crop&q=80')] bg-cover bg-center"></div>
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <span className="text-amber-500 font-semibold text-sm md:text-base tracking-wide block mb-3">إدارة ذكية وحكيمة للميزانيات القانونية للشركات</span>
          <h1 className="text-3xl md:text-5xl font-bold mb-6 text-slate-100 leading-tight">تقدير التكاليف وحوكمة ميزانيات القضايا</h1>
          <p className="text-slate-300 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
            نساعد عملائنا من قطاع الأعمال على التنبؤ الدقيق بكافة النفقات القانونية من خلال نماذج متطورة لتحليل وتقدير تكاليف الخدمات والتقاضي، مما يحقق الكفاءة المالية ويمنع الهدر المالي.
          </p>
        </div>
      </section>

      {/* Analytical & Financial Optimization Focus */}
      <section className="max-w-6xl mx-auto py-20 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl md:text-4xl font-bold text-slate-800 mb-6 leading-tight">
              لماذا يُعد تقدير التكاليف خطوة جوهرية قبل التعاقد؟
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              إن غياب المنهجية الواضحة في تقدير تكاليف الخدمات القانونية يضع الشركات في مواجهة أعباء مالية متراكمة ومتفرعة قد تؤثر على ميزانياتها التشغيلية والتدفقات النقدية.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              لذلك نعتمد في مكتبنا بروتوكولاً مالياً دقيقاً يفصل بين الأتعاب المهنية الأساسية وبين المصاريف الطارئة والرسوم القضائية. نقوم بإعداد جدول تكاليف تقديري يوضح مراحل سير الملف القانوني والتدفقات المالية المطلوبة عند كل مرحلة إجرائية، مما يتيح للإدارات المالية في منشأتك اتخاذ قرارات مدروسة ومستندة إلى الشفافية المطلقة.
            </p>
          </div>
          
          {/* Key Advantages Checklist Box */}
          <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 space-y-6">
            <h3 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-amber-600" /> محددات السياسة التقديرية للشركات:
            </h3>
            
            <div className="flex gap-3 items-start">
              <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0 mt-1" />
              <p className="text-gray-600 text-sm leading-relaxed">**منع المفاجآت المالية:** التزام كتابي بعدم فوترة أي عمل خارج العقد الأساسي دون ملحق مالي معتمد.</p>
            </div>

            <div className="flex gap-3 items-start">
              <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0 mt-1" />
              <p className="text-gray-600 text-sm leading-relaxed">**ربط التدفقات بالمخرجات:** ربط سداد الدفعات المالية بتحقيق إنجازات إجرائية ملموسة في الملف.</p>
            </div>

            <div className="flex gap-3 items-start">
              <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0 mt-1" />
              <p className="text-gray-600 text-sm leading-relaxed">**جدولة السداد المرن:** تصميم خطط سداد ميسرة تتناسب مع حجم الأعمال ونموذج المنشآت الصغيرة والمتوسطة.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Grid Capability Matrix Breakdown */}
      <section className="bg-slate-900 text-white py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-4xl font-bold mb-3">ركائز تقدير الأتعاب وحوكمة النفقات</h2>
            <p className="text-slate-400 max-w-xl mx-auto">آليات علمية ونظامية تضمن احتساباً عادلاً للأتعاب بما يعكس القيمة القانونية الحقيقية لخدماتنا</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {estimationFactors.map((factor, index) => (
              <div key={index} className="bg-slate-800/60 p-8 rounded-xl border border-slate-700 hover:border-amber-500 transition-all duration-300">
                <div className="bg-slate-700/50 p-3 rounded-lg w-fit mb-5">
                  {factor.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-100 mb-2">{factor.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{factor.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Context on Fees Recovery (Saudi Law Context) */}
      <section className="max-w-4xl mx-auto py-16 px-4">
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-6 flex gap-4 items-start">
          <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-slate-800 mb-1">استرداد تكاليف ومصاريف التقاضي نظاماً:</h4>
            <p className="text-amber-950 text-sm leading-relaxed">
              وفقاً للتعديلات التشريعية الحديثة ونظام التكاليف القضائية في المملكة العربية السعودية، صَدَرت ضوابط صريحة تمنح الطرف المحكوم له الحق في **إلزام الطرف الخاسر بتحمل كامل التكاليف القضائية وأتعاب المحاماة الفعلية** التي تكبدها لانتزاع حقه. إن توثيق الأتعاب بعقود واضحة وفواتير نظامية هو مستندك الرئيسي أمام المحكمة التجارية لاسترداد كامل ميزانيتك القانونية المدفوعة من الخصم.
            </p>
          </div>
        </div>
      </section>

      {/* Active Lead Gen Conversion Block */}
      <section className="max-w-4xl mx-auto pb-20 px-4 text-center">
        <div className="inline-flex p-3 bg-slate-100 rounded-full text-slate-800 mb-4">
          <BadgeDollarSign className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">هل تحتاج إلى تقدير ميزانية متكاملة لملفك القانوني؟</h3>
        <p className="text-gray-500 text-sm max-w-xl mx-auto mb-6">
          أرسل لنا ملخصاً بنطاق العمل المطلوب أو تفاصيل النزاع التجاري، وسيقوم فريقنا المالي والقانوني المشترك بإعداد وثيقة تقدير تكاليف شاملة ومغلقة لشركتك.
        </p>
        <button className="bg-amber-600 text-white font-medium text-sm px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors shadow-md inline-flex items-center gap-2">
          طلب باقة تحليل وتقدير تكاليف المشروع
          <ChevronLeft className="w-4 h-4" />
        </button>
      </section>

    </div>
  );
}